import { useState } from 'react'
import styled from 'styled-components'

import NoticeEventListDropdown from '~/features/notice/components/NoticeEventListDropdown'
import NoticeList from '~/features/notice/components/NoticeList'
import NoticeTabs from '~/features/notice/components/NoticeTabs'
import SearchBar from '~/features/notice/components/SearchBar'
import {
  useNoticeCategory,
  useNoticeEventList,
  useNoticeList,
} from '~/features/notice/queries/useNotice'
import { type Filters } from '~/features/notice/types/notice'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import Pagination from '~/shared/components/pagination/Pagination'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'

function NoticePage() {
  const NoticeBgMobile = `${import.meta.env.VITE_STATIC_ENDPOINT}/assets/background/notice-bg-390.png`
  const NoticeBgPc = `${import.meta.env.VITE_STATIC_ENDPOINT}/assets/background/notice-bg-1920.png`
  const viewport = useViewport()
  const isMobile = viewport < theme.deviceSizes.medium
  const backgroundImage =
    viewport < theme.deviceSizes.medium ? NoticeBgMobile : NoticeBgPc

  const [filters, setFilters] = useState<Filters>({
    page: 1,
    row_per_page: isMobile ? 5 : 10,
    category_code: 'C0200001',
    event_id: 0,
    keyword: '',
  })
  const { data: tabOptions, isLoading: categoryListLoading } =
    useNoticeCategory()
  const { data: dropdownOptions, isLoading: eventListLoading } =
    useNoticeEventList()

  const { data: notice, isLoading: noticeLoading } = useNoticeList(filters)

  if (categoryListLoading || eventListLoading || noticeLoading)
    return <LoadingScreen />

  const { pageInfo, list } = notice!

  const handleDropdownChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const eventId = Number(event.target.value)
    setFilters((prevFilters) => ({ ...prevFilters, event_id: eventId }))
  }

  const handleChangeTab = (tab: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      category_code: tab,
    }))
  }

  const handleChangePage = (
    _event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setFilters((prevFilters) => ({ ...prevFilters, page }))
  }

  const handleSearch = (keyword: string) => {
    setFilters((prevFilters) => ({ ...prevFilters, keyword }))
  }

  return (
    <Wrapper>
      <BackgroundImage $src={backgroundImage} />
      <Title>공지사항</Title>
      <Container>
        <NoticeTabs
          tabContents={tabOptions}
          selectedTab={filters.category_code}
          $size="sm"
          setSelectTab={handleChangeTab}
        />
        <FilterBox>
          <NoticeEventListDropdown
            options={dropdownOptions}
            onChange={handleDropdownChange}
            value={filters.event_id}
          />
          <SearchBar keyword={filters.keyword} onSearch={handleSearch} />
        </FilterBox>
        <NoticeList data={list} />
        {list.length > 0 && (
          <PaginationWrapper
            page={filters.page}
            count={pageInfo.max_page}
            onChange={handleChangePage}
          />
        )}
      </Container>
    </Wrapper>
  )
}

export default NoticePage

const Wrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 100vw;
  max-width: 1080px;
  min-height: calc(100vh - ${theme.size.headerHeight}px);
  margin: 0 auto;
  margin-top: calc(80px + ${theme.size.headerHeight}px);
  margin-bottom: 120px;

  @media ${theme.device.large} {
    padding: 0 60px;
  }

  @media ${theme.device.medium} {
    gap: 24px;
    align-items: start;

    width: 100vw;
    height: auto;
    margin-top: calc(56px + ${theme.size.headerHeight}px);
    padding: 0 20px;
  }
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Heavy, sans-serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 59px;
  text-align: left;
  white-space: pre-line;

  @media ${theme.device.medium} {
    width: 100%;
    height: auto;
    font-size: 28px;
    line-height: 40px;
  }
`
const Container = styled.div`
  z-index: 1;

  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: start;

  box-sizing: border-box;
  width: 100%;
  padding: 36px 24px;

  background-color: white;
  border-radius: 12px;
`

const BackgroundImage = styled.div<{ $src: string }>`
  position: fixed;
  z-index: ${theme.zIndex.belowBackground};
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  background-image: url(${(props) => props.$src});
  background-attachment: fixed;
  background-size: cover;
`

const FilterBox = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    width: 100%;
  }
`

const PaginationWrapper = styled(Pagination)`
  display: flex;
  justify-content: center;
  margin: 0 auto;

  button {
    font-size: 14px;
    font-weight: 500;
    font-style: normal;
    line-height: 20px;
    color: #6b7280;
  }

  .Mui-selected {
    color: ${theme.colors.mint_3};
    background-color: transparent;
    border: none;
    border-radius: 10px;
  }
`
